import dayjs from 'dayjs';
import {
  IGetLeadApproachingReq,
  IGetLeadApproachingResp,
  ILeadDetail,
  ILeadStatus,
  ITagItem,
  IUpdateTicketApproachLead,
} from 'interfaces/lead';
import { useEffect, useState } from 'react';
import { fileService, leadServices } from 'services/index';

const useLead = () => {
  const [isGetLeadStatuses, setIsGetLeadStatuses] = useState<boolean>(false);
  const [isGetLeadDetail, setIsGetLeadDetail] = useState<boolean>(false);
  const [isUpdateTicketLead, setIsUpdateTicketLead] = useState<boolean>(false);

  const [leadStatuses, setLeadStatuses] = useState<ILeadStatus[]>();
  const [lead, setLead] = useState<ILeadDetail>();

  const getLeadStatuses = async () => {
    try {
      setIsGetLeadStatuses(true);
      const res = await leadServices.getLeadStatus();
      if (res) {
        setLeadStatuses(res.leadStatuses);
      }
    } finally {
      setIsGetLeadStatuses(false);
    }
  };

  const getLeadDetail = async (leadId: number) => {
    try {
      setIsGetLeadDetail(true);
      const res = await leadServices.getLeadDetail(leadId);
      if (res) {
        setLead(res);
      }
    } finally {
      setIsGetLeadDetail(false);
    }
  };

  const updateTicketLead = async (payload: IUpdateTicketApproachLead) => {
    try {
      setIsUpdateTicketLead(true);
      await leadServices.updateTicketApproachLead(payload);
    } finally {
      setIsUpdateTicketLead(false);
    }
  };

  return {
    isGetLeadStatuses,
    leadStatuses,
    getLeadStatuses,
    isGetLeadDetail,
    lead,
    getLeadDetail,
    isUpdateTicketLead,
    updateTicketLead,
  };
};

const useTag = () => {
  const [isGetTags, setIsGetTags] = useState<boolean>(false);
  const [isCreateTags, setIsCreateTags] = useState<boolean>(false);

  const [tags, setTags] = useState<ITagItem[]>([]);

  const getTags = async (searchTag?: string) => {
    try {
      setIsGetTags(true);
      const res = await leadServices.getTags(searchTag);
      if (res) {
        setTags(res.tags);
      }
    } finally {
      setIsGetTags(false);
    }
  };

  const createTags = async (tagNames: string[]) => {
    try {
      setIsCreateTags(true);
      const res = await leadServices.createTags(tagNames);
      if (res) {
        return res.tags;
      }
    } finally {
      setIsCreateTags(false);
    }
  };

  return {
    isGetTags,
    tags,
    getTags,
    isCreateTags,
    createTags,
  };
};

const useApproachingLead = () => {
  const [isGetApproaching, setIsGetApproaching] = useState<boolean>(false);
  const [isExport, setIsExport] = useState<boolean>(false);
  const [leadApproaching, setLeadApproaching] = useState<
    IGetLeadApproachingResp
  >();
  const [filters, setFilters] = useState<IGetLeadApproachingReq>();
  const [exportFilters, setExportFilters] = useState<IGetLeadApproachingReq>();

  const getLeadApproaching = async () => {
    try {
      setIsGetApproaching(true);
      const res = await leadServices.getLeadApproaching({ ...filters });
      if (res) {
        setLeadApproaching(res);
      }
    } finally {
      setIsGetApproaching(false);
    }
  };

  const exportLeadApproaching = async () => {
    try {
      setIsExport(true);
      await leadServices
        .exportLeadApproaching({ ...exportFilters })
        .then(res => {
          fileService.downloadFileToDevice(
            res.filePath,
            `tiepcanKH_${dayjs().format('DDMMYYYY')}.xlsx`
          );
        });
    } finally {
      setIsExport(false);
    }
  };

  useEffect(() => {
    if (!isExport && exportFilters) {
      exportLeadApproaching().then();
    }
  }, [exportFilters]);

  useEffect(() => {
    if (!isGetApproaching && filters) {
      getLeadApproaching().then();
    }
  }, [filters]);

  return {
    isExport,
    setExportFilters,
    isGetApproaching,
    leadApproaching,
    filters,
    setFilters,
  };
};

export default {
  useLead,
  useTag,
  useApproachingLead,
};
